import { useEffect } from 'react';
import {
    createBrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

//
import sentryConfig from './sentryConfig.ts';

export function sentryInit() {
    Sentry.init({
        ...sentryConfig,

        // See docs for support of different versions of variation of React router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [
                    new RegExp(`^${import.meta.env.VITE_APP_DJANGO_API_ENTRYPOINT}`),
                    new RegExp(`^${import.meta.env.VITE_APP_GRAPHQL_HTTP_ENDPOINT}`),
                ],
            }),
            Sentry.httpClientIntegration({
                failedRequestStatusCodes: [400, [500, 599]], // Default is [[500, 599]]
            }),
        ],

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            'localhost',
            /^\//,
            new RegExp(`^${import.meta.env.VITE_APP_DJANGO_API_ENTRYPOINT}`),
            new RegExp(`^${import.meta.env.VITE_APP_GRAPHQL_HTTP_ENDPOINT}`),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
        // Session Replay — sample 10% of sessions in prod, 100% on errors
        replaysSessionSampleRate: import.meta.env.VITE_APP_ENVIRONMENT === 'production' ? 0.1 : 0,
        replaysOnErrorSampleRate: 1.0,
    });

    return {
        sentryCreateBrowserRouter: Sentry.wrapCreateBrowserRouterV6(createBrowserRouter),
    };
}

export function updateSentryUser<T extends { id?: string; username?: string; email?: string }>(
    userData: T | null = null
) {
    Sentry.setUser(
        userData
            ? {
                  id: userData.id,
                  username: userData.username,
                  email: userData.email,
              }
            : null
    );
}
